import React, { useEffect, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Building2, MapPin, Search, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Google-Places-backed hotel picker. On pick, calls /api/hotels/place-details
 * and pushes name / address / city / country / phone / image / place_id back
 * to the parent via `onPick`.
 */
export default function HotelPlaceSelect({ value, onPick, testid = "hotel-place-picker" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pickingId, setPickingId] = useState("");
  const debRef = useRef();

  useEffect(() => {
    if (!open) return;
    clearTimeout(debRef.current);
    const query = q.trim();
    if (query.length < 2) { setResults([]); return; }
    debRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/hotels/places-search`, {
          params: { q: query, limit: 10 },
        });
        setResults(Array.isArray(data) ? data : []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(debRef.current);
  }, [q, open]);

  const pick = async (hotel) => {
    setPickingId(hotel.place_id);
    try {
      const { data } = await api.get(`/hotels/place-details`, {
        params: { place_id: hotel.place_id },
      });
      onPick({
        place_id: data.place_id,
        name: data.name,
        address: data.address,
        city: data.city,
        country: data.country,
        phone: data.phone,
        image: data.image,
      });
      setOpen(false);
      setQ("");
    } catch (e) {
      // If details endpoint fails, fall back to the search result data
      onPick({
        place_id: hotel.place_id,
        name: hotel.name,
        address: hotel.address,
        city: "",
        country: "",
        phone: "",
        image: hotel.image,
      });
      setOpen(false);
      setQ("");
    } finally {
      setPickingId("");
    }
  };

  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1.5">
        <Search size={11} /> Find hotel on Google
      </div>
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setQ(""); }}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={testid}
            className="w-full border border-emerald-300 bg-emerald-50/40 rounded px-3 py-2 text-sm text-left flex items-center gap-2 hover:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 transition"
          >
            <Building2 size={14} className="text-emerald-600 shrink-0" />
            <span className={value ? "text-slate-900 truncate" : "text-slate-500 truncate"}>
              {value || "Search a hotel — auto-fills all fields below"}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[420px] p-0 overflow-hidden" data-testid={`${testid}-popover`}>
          <div className="p-2 border-b border-slate-100">
            <input
              autoFocus
              data-testid={`${testid}-input`}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="e.g. Citymax Bur Dubai, Taj Palace Mumbai…"
              className="w-full border border-slate-200 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
            />
          </div>
          <div className="max-h-[340px] overflow-y-auto">
            {loading && <div className="px-3 py-3 text-xs text-slate-400 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Searching Google…</div>}
            {!loading && q.trim().length < 2 && (
              <div className="px-3 py-3 text-xs text-slate-400">Type at least 2 characters</div>
            )}
            {!loading && q.trim().length >= 2 && results.length === 0 && (
              <div className="px-3 py-3 text-xs text-slate-400">No hotels found on Google</div>
            )}
            {results.map((h) => (
              <button
                key={h.place_id}
                type="button"
                data-testid={`${testid}-option-${h.place_id}`}
                disabled={!!pickingId}
                onClick={() => pick(h)}
                className={`w-full text-left px-3 py-2.5 flex items-start gap-3 border-b border-slate-50 last:border-b-0 ${pickingId === h.place_id ? "bg-emerald-50" : "hover:bg-slate-50"} disabled:opacity-60`}
              >
                <div className="w-14 h-11 shrink-0 rounded overflow-hidden bg-slate-100 flex items-center justify-center">
                  {h.image ? (
                    <img src={h.image} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : (
                    <Building2 size={16} className="text-slate-400" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-900 truncate font-medium">{h.name}</div>
                  <div className="text-xs text-slate-500 truncate flex items-center gap-1">
                    <MapPin size={10} /> {h.address}
                  </div>
                  {h.rating > 0 && (
                    <div className="text-[11px] text-amber-600 mt-0.5">
                      ★ {h.rating.toFixed(1)} <span className="text-slate-400">({h.review_count.toLocaleString()} reviews)</span>
                    </div>
                  )}
                </div>
                {pickingId === h.place_id && <Loader2 size={12} className="animate-spin text-emerald-600 mt-2" />}
              </button>
            ))}
          </div>
          <div className="px-3 py-2 text-[10px] text-slate-400 border-t border-slate-100 bg-slate-50/50">
            Results powered by Google Places
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
